/**
 * Route renderer that keeps recently visited screens mounted but hidden, so returning to a screen
 * shows it in the next frame instead of mounting it again and waiting for its requests.
 *
 * Each top-level path segment (`/week`, `/teams/...`, `/agent`) owns one slot. The active slot
 * renders the live router location; every other slot is wrapped in React's `<Activity
 * mode="hidden">`, which keeps its state and DOM (as `display: none`), disconnects its effects and
 * store subscriptions, and skips rendering it. Revealing a slot reconnects its effects, so each
 * screen's own mount requests run again and refresh what it shows (stale-while-revalidate).
 *
 * Cost model: a reveal is one commit that toggles the slot's root nodes and re-runs its effects;
 * the screen does not re-render when its location is unchanged. Memory grows with the number of
 * retained screens, bounded by `KEEP_ALIVE_LIMIT`; the least recently shown slot is unmounted
 * first. Slots keep their mount order, so a navigation never moves a retained screen's DOM; a
 * hidden screen's nodes stay in the document, so DOM queries that may match several screens
 * must filter for visible elements.
 *
 * @module apps/web/src/components/layout/KeepAliveRoutes
 */
import {
  Activity,
  createContext,
  memo,
  useCallback,
  useContext,
  useState,
  type ContextType,
  type ReactNode,
} from 'react';
import {
  NavigationType,
  Routes,
  UNSAFE_LocationContext as LocationContext,
} from 'react-router-dom';

/** Maximum number of mounted screens, including the visible one (8). */
export const KEEP_ALIVE_LIMIT = 8;

/** Slot keys that are never retained once left: `''` only redirects. */
const NOT_RETAINED = new Set(['']);

/** The router's location context value, as React Router provides it. */
type LocationValue = ContextType<typeof LocationContext>;

/** One mounted screen. */
interface Slot {
  /** First path segment that selects this slot. */
  key: string;
  /** Location context this slot renders with; frozen while hidden. */
  context: LocationValue;
  /** Activation order; the smallest is evicted first. */
  shown: number;
}

/** The slot key for `pathname`: its first segment, `''` for the root. */
export function keepAliveKey(pathname: string): string {
  return pathname.split('/')[1] ?? '';
}

/** Whether two location contexts describe the same place, so a slot need not re-render. */
function samePlace(a: LocationValue, b: LocationValue): boolean {
  return (
    a.location.pathname === b.location.pathname &&
    a.location.search === b.location.search &&
    a.location.hash === b.location.hash &&
    a.location.state === b.location.state
  );
}

/** Pre-render a route's screen hidden, ahead of a likely navigation to it. */
type Prerender = (pathname: string) => void;

/** Provides `prerender` to screens that know where the user goes next. */
const PrerenderContext = createContext<Prerender>(() => {});

/**
 * Return a function that mounts `pathname`'s screen hidden (rendered at idle priority, effects
 * not started) so a later navigation there only reveals it. A no-op outside `KeepAliveRoutes` or
 * when the screen is already mounted.
 */
export function usePrerenderRoute(): Prerender {
  return useContext(PrerenderContext);
}

/** Inputs of `KeepAliveRoutes`. */
interface KeepAliveRoutesProps {
  /** `<Route>` elements. Must be referentially stable (for example a module constant), or every hidden screen re-renders whenever the parent does. */
  children: ReactNode;
}

/** Render `children` routes with one retained, hidden-when-inactive screen per path segment. */
export function KeepAliveRoutes({ children }: KeepAliveRoutesProps) {
  const live = useContext(LocationContext);
  const key = keepAliveKey(live.location.pathname);
  const [slots, setSlots] = useState<Slot[]>(() => [{ key, context: live, shown: 0 }]);

  const active = slots.find(slot => slot.key === key);
  const latest = slots.reduce((max, slot) => Math.max(max, slot.shown), 0);
  if (!active || active.shown !== latest || !samePlace(active.context, live)) {
    // Derived state: adopt the new location during render, before anything commits.
    // Update the slot in place (or append it): reordering would move retained screens' DOM.
    const slot: Slot = {
      key,
      context: active && samePlace(active.context, live) ? active.context : live,
      shown: latest + 1,
    };
    const next = slots
      .filter(item => item.key === key || !(NOT_RETAINED.has(item.key) && item.shown === latest))
      .map(item => (item.key === key ? slot : item));
    if (!active) next.push(slot);
    while (next.length > KEEP_ALIVE_LIMIT) {
      const oldest = next.reduce((min, slot) => (slot.shown < min.shown ? slot : min));
      next.splice(next.indexOf(oldest), 1);
    }
    setSlots(next);
  }

  const prerender = useCallback<Prerender>(pathname => {
    const target = keepAliveKey(pathname);
    setSlots(current => {
      if (current.some(slot => slot.key === target) || current.length >= KEEP_ALIVE_LIMIT)
        return current;
      const context: LocationValue = {
        navigationType: NavigationType.Push,
        location: { pathname, search: '', hash: '', state: null, key: 'prerender' },
      };
      // shown -1 keeps it out of the activation order until it is first visited.
      return [...current, { key: target, context, shown: -1 }];
    });
  }, []);

  return (
    <PrerenderContext.Provider value={prerender}>
      {slots.map(slot => (
        <Activity key={slot.key} mode={slot.key === key ? 'visible' : 'hidden'}>
          <KeepAliveSlot context={slot.context}>{children}</KeepAliveSlot>
        </Activity>
      ))}
    </PrerenderContext.Provider>
  );
}

/** Inputs of `KeepAliveSlot`. */
interface KeepAliveSlotProps {
  /** Location this slot's routes match against. */
  context: LocationValue;
  /** The shared, stable `<Route>` elements. */
  children: ReactNode;
}

/**
 * One retained screen: matches `children` routes against the slot's own location. Memoized, so a
 * hidden slot with an unchanged location never re-renders.
 * @component KeepAliveSlot
 */
const KeepAliveSlot = memo(function KeepAliveSlot({ context, children }: KeepAliveSlotProps) {
  return (
    <LocationContext.Provider value={context}>
      <Routes>{children}</Routes>
    </LocationContext.Provider>
  );
});
